import { ItemTypeCategory } from '@/graphql/generatedTypes';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { ContainerStationReducedFragment } from '../graphql/fragments/containerStationReduced.generated';
import i18n from '@/utils/i18n';
import { download } from '@/utils/download';
import { LayoutStoreInstance } from '../LayoutStore';
import QRCodeStyling from 'qr-code-styling';
import { FaFileExcel, FaFileUpload, FaPlus } from 'react-icons/fa';
import { Routes } from '@/utils/routes';
import { useCreateManyContainerStationsMutation } from '../graphql/mutations/createManyContainerStations.generated';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../providers/ViewerContextProvider';
import { Communicator } from '../tools/Communication';
import {
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
} from 'reactstrap';
import CustomPagination from './CustomPagination';
import { CustomModal } from './CustomModal';
import { useStoresWithContainerStationsQuery } from '../graphql/queries/storesWithContainerStations.generated';
import { CustomTable } from './Table';
import { getArrayBuffer } from '@/utils/bufferUtils';
import { ItemTypeSelector } from './ItemTypeSelector';
import TrackableItemDisplay from './TrackableItem/TrackableItemDisplay';
import CleanOrDirtyText from './CleanOrDirtyText';
import ContainerStationStatusBadge from './ContainerStation/ContainerStationStatusBadge';

export type ContainerStationWithQrCode = ContainerStationReducedFragment & {
  qrCode: string;
};

export const CONTAINER_STATION_QR_CODE_PREFIX = 'HTTP://VYT.TO/CS/';

export const CONTAINER_STATION_PAGE_SIZE = 20;

type ContainerStationsListProps = {
  containerStations: ContainerStationReducedFragment[];
  loading: boolean;
  loadMore: (skip: number, take: number) => void;
  storeTextId?: string;
  total: number;
  onClick: (containerStation: ContainerStationReducedFragment) => void;
  showAddButton?: boolean;
  allowSelectingStore?: boolean;
};

export function ContainerStationsList(props: ContainerStationsListProps) {
  const [containerStations, setContainerStations] = useState<
    ContainerStationReducedFragment[]
  >([]);
  const { total: totalPages, loadMore, onClick, showAddButton = false } = props;
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadMore(
      (page - 1) * CONTAINER_STATION_PAGE_SIZE,
      CONTAINER_STATION_PAGE_SIZE,
    );
  }, [page]);

  const viewer = useContext(ViewerContext);

  const [creatingContainerStations, setCreatingContainerStations] =
    useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isUploadFileModalOpen, setIsUploadFileModelOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [callbackId, setCallbackId] = useState('');
  const [updateFile, setUpdateFile] = useState<File | null>(null);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  const [progressIntervalId, setProgressIntervalId] = useState(0);
  const [newStationCount, setNewStationCount] = useState('0');
  const [storeTextId, setStoreTextId] = useState<string>(
    props.storeTextId || '',
  );
  const [newStationsTypeId, seNewStationsTypeId] = useState<string>('1');

  const { data: storesWithContainerStations, loading: loadingStores } =
    useStoresWithContainerStationsQuery({
      variables: {
        skip: 0,
        take: 50,
      },
      skip: !props.allowSelectingStore,
      fetchPolicy: 'cache-and-network',
      onError: (error) => {
        console.log(error);
        LayoutStoreInstance.setError(
          i18n.t('ContainerStationList:failedToLoadStores') + error.message,
        );
      },
    });

  const [createContainerStations] = useCreateManyContainerStationsMutation({
    onError: (error) => {
      LayoutStoreInstance.setError(
        i18n.t('ContainerStation:failedToCreateStations') + error.message,
      );
    },
  });

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  useEffect(() => {
    setContainerStations([]);
    setContainerStations(props.containerStations);
  }, [props.containerStations]);

  async function downloadCsv(
    containerStations: ContainerStationReducedFragment[],
  ) {
    try {
      const csv = [
        [
          i18n.t('Common:id'),
          i18n.t('Common:name'),
          i18n.t('ContainerStation:typeId'),
          i18n.t('Common:type'),
          i18n.t('ContainerStation:containers'),
          i18n.t('ContainerStation:qrCodeText'),
          i18n.t('ContainerStation:epcTag'),
        ],
      ];

      containerStations.forEach((cs) => {
        csv.push([
          cs.id,
          cs.name,
          cs.item_type.id.toString(),
          cs.item_type.name,
          cs.total_stock.toString(),
          `${CONTAINER_STATION_QR_CODE_PREFIX}${cs.id}`,
          cs.epc_tag,
        ]);
      });

      const csvContent = csv.map((row) => row.join(',')).join('\n');

      download(
        new Blob([csvContent], { type: 'text/csv' }),
        'ContainerStations.csv',
      );
    } catch (error) {
      console.log(JSON.stringify(error));

      LayoutStoreInstance.setError(i18n.t('Common:failedToExportCsv'));
    }
  }

  const pageCount = Math.ceil(totalPages / CONTAINER_STATION_PAGE_SIZE);

  async function downloadXls(stations: ContainerStationReducedFragment[]) {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      worksheet.properties.defaultRowHeight = 100;
      worksheet.properties.defaultColWidth = 20;

      worksheet.addRow([
        i18n.t('Common:id'),
        i18n.t('Common:name'),
        i18n.t('Common:type'),
        i18n.t('ContainerStation:containers'),
        i18n.t('ContainerStation:qrCodeText'),
        i18n.t('ContainerStation:epcTag'),
        i18n.t('Common:qrCode'),
      ]);

      const qrCodeBuffers = await Promise.all(
        stations.map(async (cs) => {
          const qrCode = getQrCodeStyling(cs.id);
          const blob = await qrCode.getRawData('png');
          return await getArrayBuffer(blob);
        }),
      );

      const imageIds = qrCodeBuffers.map((buf) =>
        buf
          ? workbook.addImage({
              buffer: buf,
              extension: 'png',
            })
          : null,
      );

      stations.forEach((cs, index) => {
        const row = worksheet.addRow([
          cs.id,
          cs.name,
          cs.item_type.name,
          cs.total_stock,
          `${CONTAINER_STATION_QR_CODE_PREFIX}${cs.id}`,
          cs.epc_tag,
        ]);
        const id = imageIds[index];
        if (id != null) {
          worksheet.addImage(id, {
            tl: { col: 6, row: row.number - 1 },
            ext: { width: 100, height: 100 },
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      download(
        new Blob([buffer], { type: 'application/octet-stream' }),
        'ContainerStations.xlsx',
      );
    } catch (error) {
      console.log(JSON.stringify(error));

      LayoutStoreInstance.setError(i18n.t('Common:failedToExportXls'));
    }
  }

  async function createStations(
    count: number,
    storeId: string,
    typeId: number,
  ) {
    try {
      setCreatingContainerStations(true);
      const result = await createContainerStations({
        variables: {
          count,
          storeId,
          type_id: typeId,
        },
      });
      if (result.data != null) {
        const stations = result.data.createManyContainerStations;
        await downloadCsv(
          stations.map((s) => ({
            ...s,
            total_stock: 0,
            store: { __typename: 'Store' as const, id: s.store_id, name: null },
            stock: [],
            status: null,
            current_item_location: null,
            item_location_name: null,
          })),
        );
        loadMore(0, CONTAINER_STATION_PAGE_SIZE);
      } else {
        LayoutStoreInstance.setError(
          i18n.t('ContainerStation:failedToCreateStations'),
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCreatingContainerStations(false);
    }
  }

  function getQrCodeStyling(id: string) {
    return new QRCodeStyling({
      width: 100,
      data: `${CONTAINER_STATION_QR_CODE_PREFIX}${id}`,
      height: 100,
      dotsOptions: {
        color: '#000000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 10,
      },
      backgroundOptions: {
        color: '#00000000',
      },
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
    });
  }

  async function handleCreateStations() {
    if (storeTextId == null || storeTextId.trim() === '') {
      return;
    }
    setCreatingContainerStations(true);
    await createStations(
      parseInt(newStationCount),
      storeTextId as string,
      parseInt(newStationsTypeId),
    );
    closeModal();
  }

  function uuid() {
    var buf = new Uint32Array(4);
    window.crypto.getRandomValues(buf);
    var idx = -1;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        idx++;
        var r = (buf[idx >> 3] >> ((idx % 8) * 4)) & 15;
        var v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  async function updateContainerStations() {
    const callbackId = uuid();

    setIsSubmitting(true);
    setCallbackId(callbackId);

    if (!updateFile) {
      return alert('Please select a file to upload');
    }

    const postPromise = Communicator.PostFileUpload(
      `/Container/UpdateContainerStationsFromCsv?callbackId=${callbackId}`,
      updateFile,
    );

    const intervalId = setInterval(async (_) => {
      const progressResult = await Communicator.Get(
        `/Container/QueryUpdateContainerProcessingStatus`,
        { callbackId },
      );
      const progress = await progressResult.json();
      if (progress.processed && progress.total) {
        setProcessed(progress.processed);
        setTotal(progress.total);
      }
    }, 5000);
    setProgressIntervalId(Number(intervalId));

    postPromise
      .then(async (postResult) => {
        if (postResult.ok) {
          LayoutStoreInstance.setSuccess('Stations updated successfully');
        } else {
          const errorResponse = await postResult.json();
          if (errorResponse.errors && errorResponse.errors.length > 0) {
            alert(`Error: ${errorResponse.errors.join(',')}`);
          } else {
            alert(`Unknown Error. Please try again.`);
          }
        }
      })
      .catch(async (postResult) => {
        const errorResponse = await postResult.json();
        if (errorResponse.errors && errorResponse.errors.length > 0) {
          alert(`Error: ${errorResponse.errors.join(',')}`);
        } else {
          alert(`Unknown Error. Please try again.`);
        }
      })
      .finally(() => {
        if (progressIntervalId) {
          clearInterval(progressIntervalId);
          setProgressIntervalId(0);
        }
        setIsSubmitting(false);
        setIsUploadFileModelOpen(false);
      });
  }

  const renderLocationWithLink = (station: ContainerStationReducedFragment) => {
    const locationName =
      station.current_item_location?.name ||
      station.item_location_name ||
      station.store?.name;

    // If no location name, show dash
    if (!locationName) {
      return '-';
    }

    // If we have store with brand_id and id, create a link
    if (station.store?.brand_id && station.store?.id) {
      return (
        <Link
          to={Routes.partnerBrandStore(
            station.store.brand_id,
            station.store.id,
          )}
        >
          {locationName}
        </Link>
      );
    }

    // Otherwise just show the text
    return locationName;
  };

  function renderContainerUpdateDialog() {
    return (
      <Modal
        isOpen={isUploadFileModalOpen}
        onClosed={() => setIsUploadFileModelOpen(false)}
        toggle={() => {
          setIsUploadFileModelOpen(!isUploadFileModalOpen);
        }}
      >
        <ModalHeader
          toggle={() => {
            setIsUploadFileModelOpen(!isUploadFileModalOpen);
          }}
        >
          {i18n.t('ContainerStation:updateFromCsv')}
        </ModalHeader>

        <ModalBody>
          {isSubmitting ? (
            <div className="text-center">
              <Progress animated value={processed} max={total} />
              <div>
                ({processed}/{total} {i18n.t('ContainerStation:processed')}).
                <br />{' '}
                <strong>{i18n.t('ContainerStation:processingTimeWarn')}</strong>
              </div>
            </div>
          ) : (
            <div>
              <FormGroup>
                <Label className="me-2">
                  {i18n.t('ContainerStation:containerStationUploadFile')}:{' '}
                </Label>
                <Input
                  id="containerStationUpload"
                  name="containerStationUpload"
                  type="file"
                  onChange={(e: any) =>
                    setUpdateFile(
                      e.target.files && e.target.files.length > 0
                        ? e.target.files[0]
                        : null,
                    )
                  }
                />
              </FormGroup>
              <Button
                onClick={updateContainerStations}
                disabled={isSubmitting || updateFile == null}
              >
                {i18n.t('Common:add')}
              </Button>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }

  return (
    <div>
      <div
        className="mb-3"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        <Button className="me-2" onClick={() => downloadXls(containerStations)}>
          <FaFileExcel className="me-1" />
          {i18n.t('Common:exportExcel')}{' '}
        </Button>
        <Button className="me-2" onClick={() => setIsUploadFileModelOpen(true)}>
          <FaFileUpload className="ms-1"></FaFileUpload>
          {i18n.t('Common:uploadFile')}
        </Button>
        {(props.storeTextId != null || props.allowSelectingStore) &&
          CanAccessPartnerData(viewer) && (
            <Button onClick={openModal}>
              <FaPlus className="me-1" />
              {i18n.t('Common:add')}
            </Button>
          )}
      </div>

      <CustomTable
        striped
        bordered
        hover
        error={undefined}
        loading={props.loading}
        headers={[
          i18n.t('Common:box'), // Changed from 'item' to 'box'
          i18n.t('Common:type'),
          i18n.t('Common:currentLocation'),
          i18n.t('Common:boxStatus'),
          i18n.t('ContainerStation:containerType'),
          i18n.t('ContainerStation:containerAmount'),
          i18n.t('Common:containerStatus'),
          ...(showAddButton ? [i18n.t('Common:add')] : []),
        ]}
        rows={containerStations.map((station) => (
          <tr
            key={station.id}
            onClick={() => {
              if (showAddButton) {
                return;
              }

              onClick(station);
            }}
          >
            <td>
              <TrackableItemDisplay
                trackableItem={station as any}
                showTrackableItemId={false}
                showLink={!showAddButton}
                linkInNewTab={false}
              />
            </td>
            <td>{station.item_type.name || '-'}</td>
            <td>{renderLocationWithLink(station)}</td>
            <td>
              <ContainerStationStatusBadge status={station.status} />
            </td>
            <td>{station.stock[0]?.name || '-'}</td>
            <td>{station?.total_stock || 0}</td>
            <td>
              {station.stock[0]?.sealed ? (
                <CleanOrDirtyText isClean />
              ) : (
                <CleanOrDirtyText isDirty />
              )}
            </td>
            {showAddButton && (
              <td>
                <Button color="primary" onClick={() => onClick(station)}>
                  {i18n.t('Common:add')}
                </Button>
              </td>
            )}
          </tr>
        ))}
      />

      <CustomPagination
        pageCount={pageCount}
        currentPage={page}
        onPageChange={setPage}
      />

      <CustomModal
        isOpen={isModalOpen}
        setIsOpen={setModalOpen}
        header={i18n.t('ContainerStation:addModalTitle')}
        body={
          <>
            <InputGroup className="mb-2">
              <InputGroupText>{i18n.t('Common:count')}</InputGroupText>
              <Input
                title={i18n.t('Common:count')}
                value={newStationCount}
                onChange={(event) =>
                  setNewStationCount(
                    event.currentTarget.value.replace(/\D/g, ''),
                  )
                }
              />
            </InputGroup>

            <InputGroup className="mb-2">
              <InputGroupText>{i18n.t('Common:type')}</InputGroupText>
              <ItemTypeSelector
                category={ItemTypeCategory.ContainerStation}
                itemTypeId={newStationsTypeId}
                setItemTypeId={seNewStationsTypeId}
              />
            </InputGroup>

            {props.allowSelectingStore && (
              <InputGroup className="mb-2">
                <InputGroupText>
                  {i18n.t('ContainerStationList:store')}
                </InputGroupText>

                <Input
                  type="select"
                  title={i18n.t('ContainerStationList:store')}
                  value={storeTextId}
                  disabled={loadingStores}
                  onChange={({ target: { value } }) => {
                    setStoreTextId(value);
                  }}
                >
                  <option value="" disabled hidden>
                    {i18n.t('ContainerStationList:selectStore')}
                  </option>
                  {storesWithContainerStations?.storesWithContainerStations.map(
                    (store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ),
                  )}
                </Input>
              </InputGroup>
            )}
          </>
        }
        acceptButtonAction={handleCreateStations}
        acceptButtonDisabled={
          storeTextId == null ||
          newStationCount === '0' ||
          creatingContainerStations
        }
        acceptButtonText={
          creatingContainerStations
            ? i18n.t('Common:loading')
            : i18n.t('Common:add')
        }
        acceptButtonIcon={<FaPlus />}
      />
      {renderContainerUpdateDialog()}
    </div>
  );
}

export default ContainerStationsList;
